/**
 * Icon Registry
 *
 * Typed registry mapping icon names to their static imports.
 * Import from here when you need a specific icon URL, or use
 * the `<Icon>` component which accepts a `name` prop.
 */

// ─── UI Icons ───────────────────────────────────────────────────────────────

import advancedSetting from "./images/ui/advancedsetting.svg";
import arrow from "./images/ui/arrow.svg";
import chevronRight from "./images/ui/chevron-right.svg";
import citation from "./images/ui/citation.svg";
import controlPanelArrow from "./images/ui/control-panel-arrow.svg";
import dataDownload from "./images/ui/dataDownload.svg";
import datanotesOff from "./images/ui/datanotes_off.svg";
import datanotesOn from "./images/ui/datanotes_on.svg";
import datanotesOnOrange from "./images/ui/datanotes_on_orange.svg";
import download from "./images/ui/download.svg";
import externalLink from "./images/ui/external-link.svg";
import findInViz from "./images/ui/find-in-viz.svg";
import findInVizNoBackground from "./images/ui/find-in-viz-no-background.svg";
import glossary from "./images/ui/glossary.svg";
import guide from "./images/ui/guide.svg";
import hoverhand from "./images/ui/hoverhand.svg";
import imageDownload from "./images/ui/imageDownload.svg";
import infoCircle from "./images/ui/info-circle.svg";
import magnifyingGlass from "./images/ui/magnifying-glass.svg";
import rankingOvertime from "./images/ui/ranking-overtime.svg";
import reload from "./images/ui/reload.svg";
import settings from "./images/ui/settings.svg";
import share from "./images/ui/share.svg";
import subregionMap from "./images/ui/subregion-map.svg";
import sortingIcon from "./images/ui/sorting-icon.svg";
import swap from "./images/ui/swap.svg";

export const uiIcons = {
  advancedSetting,
  arrow,
  chevronRight,
  citation,
  controlPanelArrow,
  dataDownload,
  datanotesOff,
  datanotesOn,
  datanotesOnOrange,
  download,
  externalLink,
  findInViz,
  findInVizNoBackground,
  glossary,
  guide,
  hoverhand,
  imageDownload,
  infoCircle,
  magnifyingGlass,
  rankingOvertime,
  reload,
  settings,
  share,
  sortingIcon,
  subregionMap,
  swap,
} as const;
export type UiIconName = keyof typeof uiIcons;

// ─── Viz Type Icons ─────────────────────────────────────────────────────────

import treemap from "./images/vizTypeIcons/treemap.svg";
import treemapWhite from "./images/vizTypeIcons/treemap-white.svg";
import geomap from "./images/vizTypeIcons/geomap.svg";
import geomapWhite from "./images/vizTypeIcons/geomap-white.svg";
import overtime from "./images/vizTypeIcons/overtime.svg";
import overtimeWhite from "./images/vizTypeIcons/overtime-white.svg";
import globalshare from "./images/vizTypeIcons/globalshare.svg";
import globalshareWhite from "./images/vizTypeIcons/globalshare-white.svg";
import productspace from "./images/vizTypeIcons/productspace.svg";
import productspaceWhite from "./images/vizTypeIcons/productspace-white.svg";
import feasibility from "./images/vizTypeIcons/feasibility.svg";
import feasibilityWhite from "./images/vizTypeIcons/feasibility-white.svg";
import rings from "./images/vizTypeIcons/rings.svg";
import ringsWhite from "./images/vizTypeIcons/rings-white.svg";

export const vizTypeIcons = {
  treemap,
  treemapWhite,
  geomap,
  geomapWhite,
  overtime,
  overtimeWhite,
  globalshare,
  globalshareWhite,
  productspace,
  productspaceWhite,
  feasibility,
  feasibilityWhite,
  rings,
  ringsWhite,
} as const;
export type VizTypeIconName = keyof typeof vizTypeIcons;

// ─── Social Icons ───────────────────────────────────────────────────────────

import applepodcast from "./images/social/applepodcast.svg";
import facebook from "./images/social/facebook.svg";
import github from "./images/social/github.svg";
import instagram from "./images/social/instagram.svg";
import linkedin from "./images/social/linkedin.svg";
import twitter from "./images/social/twitter.svg";
import x from "./images/social/x.svg";
import youtube from "./images/social/youtube.svg";

export const socialIcons = {
  applepodcast,
  facebook,
  github,
  instagram,
  linkedin,
  twitter,
  x,
  youtube,
} as const;
export type SocialIconName = keyof typeof socialIcons;

// ─── Logos ──────────────────────────────────────────────────────────────────

import glLogoBlack from "./images/logos/GL_logo_black.png";
import glLogoWhite from "./images/logos/GL_logo_white.png";
import atlasLogo from "./images/logos/Atlas2_logo.png";
import atlasCidLogoLight from "./images/logos/Atlas2_CID_logo_light.svg";
import glAtlasFavicon from "./images/logos/GL_Atlas_favicon.png";
import harvardGlLogo from "./images/logos/Harvard_GL_logo.png";
import vizHubIcon from "./images/logos/VizHubIcon.svg";
export const logos = {
  glLogoBlack,
  glLogoWhite,
  atlasLogo,
  atlasCidLogoLight,
  glAtlasFavicon,
  harvardGlLogo,
  vizHubIcon,
} as const;
export type LogoName = keyof typeof logos;

// ─── Union type ─────────────────────────────────────────────────────────────

export type IconName = UiIconName | VizTypeIconName | SocialIconName;

/** Look up any icon URL by name. */
export const allIcons: Record<IconName, string> = {
  ...uiIcons,
  ...vizTypeIcons,
  ...socialIcons,
};
